import { Accessor, createMemo, createResource } from 'solid-js';
import { isServer } from 'solid-js/web';
import { SiteDomain, SiteInfo, TermsAndConditions, UserTypeTerms } from '../../types/app-state';
import { atosGlobalCountrySlugToCountryCode } from '../../tools/countries-data';
import { UserType } from '../privacy-policy-modal/privacy-policy-modal';
import { FormValue, Localize } from './forms-types';

type CreateCountryTermsArgs = {
    formValues: Accessor<FormValue>;
    siteInfo: SiteInfo;
    // `allSiteDomains` (unfiltered by siteType) is used for country→siteId/slug lookups so atos-care forms can still resolve atos-medical country sites for per-country consent and terms.
    allSiteDomains: SiteDomain[];
    termsAndConditions: TermsAndConditions;
    localize: Localize;
};

type ConsentSettings = {
    hcpConsentType: string;
    patientConsentType: string;
};

const DEFAULT_CONSENT_SETTINGS: ConsentSettings = {
    hcpConsentType: 'single',
    patientConsentType: 'single',
};

export const createCountryTerms = (args: CreateCountryTermsArgs) => {
    const { formValues, siteInfo, allSiteDomains, termsAndConditions, localize } = args;

    // Get the selected country's ISO code (e.g., "SE", "KR")
    const getSelectedCountryCode = () => {
        const country = formValues()?.country;
        if (country && typeof country === 'object' && 'code' in country) {
            return country.code;
        }
        return undefined;
    };

    // Get the siteId for the selected country
    // Matches via country code → site slug mapping instead of country name
    // If the current site is for the same country, prefer it (handles language variants like canada-fr)
    // Falls back to international site if no matching country found
    const getSelectedCountrySiteId = createMemo(() => {
        const countryCode = getSelectedCountryCode();
        if (countryCode) {
            // If current site matches the selected country, use it (respects language variants)
            const currentSiteCountryCode = siteInfo?.siteSlug ? atosGlobalCountrySlugToCountryCode[siteInfo.siteSlug] : undefined;
            if (currentSiteCountryCode === countryCode) {
                return siteInfo?.siteId;
            }
            const matchingSite = allSiteDomains.find(site =>
                atosGlobalCountrySlugToCountryCode[site.siteSlug] === countryCode
            );
            if (matchingSite) {
                return matchingSite.siteId;
            }
        }
        // Fall back to international site for countries without dedicated privacy policy
        const internationalSite = allSiteDomains.find(site => site.siteSlug === 'international');
        return internationalSite?.siteId || siteInfo?.siteId;
    });

    // Fetch terms and conditions for the selected country
    // Skip fetching on server since relative URLs don't work during SSR
    const [countryTerms] = createResource(
        getSelectedCountrySiteId,
        async (targetSiteId: string) => {
            // Skip fetching on server - will fetch on client after hydration
            if (isServer) return null;
            if (!targetSiteId) return null;
            try {
                const response = await fetch('/api-client/terms-and-conditions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ siteId: targetSiteId }),
                });
                const data = await response.json();
                if (data.success && data.termsAndConditions) {
                    return data.termsAndConditions as TermsAndConditions;
                }
                return null;
            } catch (error) {
                console.error('[Forms] Error fetching terms:', error);
                return null;
            }
        }
    );

    // Get the selected country slug for fetching consent settings
    // Maps the selected country code to the Atos Global country slug
    // If the current site is for the same country, prefer it (handles language variants like switzerland-fr)
    const getSelectedCountrySlug = createMemo(() => {
        const countryCode = getSelectedCountryCode();
        if (countryCode) {
            // If current site matches the selected country, use its slug (respects language variants)
            const currentSiteCountryCode = siteInfo?.siteSlug ? atosGlobalCountrySlugToCountryCode[siteInfo.siteSlug] : undefined;
            if (currentSiteCountryCode === countryCode && siteInfo?.siteSlug) {
                return siteInfo.siteSlug;
            }
            const matchingSite = allSiteDomains.find(site =>
                atosGlobalCountrySlugToCountryCode[site.siteSlug] === countryCode
            );
            if (matchingSite?.siteSlug) {
                return matchingSite.siteSlug;
            }
        }
        return 'international';
    });

    // Fetch consent type settings for the selected country from Global Forms
    // Returns both hcpConsentType and patientConsentType for user-type based consent
    // Skip fetching on server since relative URLs don't work during SSR
    const [countryConsentSettings] = createResource(
        getSelectedCountrySlug,
        async (countrySlug: string): Promise<ConsentSettings> => {
            // Skip fetching on server - will fetch on client after hydration
            if (isServer) {
                return DEFAULT_CONSENT_SETTINGS;
            }
            if (!countrySlug || countrySlug === '*') {
                return DEFAULT_CONSENT_SETTINGS;
            }
            try {
                const response = await fetch('/api-client/country-consent-type', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ countryCode: countrySlug }),
                });
                const data = await response.json();
                return {
                    hcpConsentType: data.hcpConsentType || 'single',
                    patientConsentType: data.patientConsentType || 'single',
                };
            } catch (error) {
                console.error('[Forms] Error fetching consent type:', error);
                return DEFAULT_CONSENT_SETTINGS;
            }
        }
    );

    // Check if the user selected "Patient" type - everything else (HCP, Relative, Other) falls back to HCP terms
    const isPatientUserType = createMemo(() => formValues().userType === 'Patient');

    // Get the user type for terms selection: 'patient' if Patient selected, 'hcp' otherwise
    const getUserTypeForTerms = (): UserType => isPatientUserType() ? 'patient' : 'hcp';

    // Check if double consent is required based on selected country and user type
    const isDoubleConsent = createMemo(() => {
        const settings = countryConsentSettings();
        if (!settings) return false;

        const userType = formValues().userType;
        // Use patientConsentType for Patient, hcpConsentType for everyone else
        if (userType === 'Patient') {
            return settings.patientConsentType === 'double';
        }
        return settings.hcpConsentType === 'double';
    });

    // Get effective terms based on user type selection
    // Returns the appropriate UserTypeTerms (hcp or patient) from the terms data
    const getEffectiveTerms = (): UserTypeTerms => {
        const terms = countryTerms() || termsAndConditions;
        const userType = getUserTypeForTerms();

        // Try to get user-type specific terms first
        if (terms?.hcp && terms?.patient) {
            return userType === 'patient' ? terms.patient : terms.hcp;
        }

        // Fall back to flat structure (backwards compatibility)
        return {
            privacyPolicy: terms.privacyPolicy,
            marketingConsent: terms.marketingConsent,
        };
    };

    // Check if the new Terms & Conditions ACF is configured (has actual content)
    // If not, we fall back to the old format (checkbox + link to privacy policy page)
    const isTermsConfigured = createMemo(() => {
        const terms = termsAndConditions;
        // Check if privacy policy content exists (the main indicator that ACF is configured)
        return !!(terms?.privacyPolicy?.content && terms.privacyPolicy.content.trim() !== '');
    });

    // Check if marketing consent content is configured
    // If not, we only show privacy policy link in single consent mode
    const hasMarketingContent = createMemo(() => {
        const terms = getEffectiveTerms();
        return !!(terms?.marketingConsent?.content && terms.marketingConsent.content.trim() !== '');
    });

    // Reactive memos for terms text - these will update when countryTerms changes
    const privacyCheckboxText = createMemo(() =>
        getEffectiveTerms()?.privacyPolicy?.checkboxText || localize('agree-to-terms', 'I have read and agree to the')
    );
    const privacyLinkText = createMemo(() =>
        getEffectiveTerms()?.privacyPolicy?.title || localize('privacy-policy', 'Privacy Policy')
    );
    const marketingCheckboxText = createMemo(() =>
        getEffectiveTerms()?.marketingConsent?.checkboxText || localize('agree-to-marketing', 'I agree to receive')
    );
    const marketingLinkText = createMemo(() =>
        getEffectiveTerms()?.marketingConsent?.title || localize('marketing-communications', 'marketing communications')
    );

    return {
        getSelectedCountryCode,
        getUserTypeForTerms,
        isDoubleConsent,
        isTermsConfigured,
        hasMarketingContent,
        privacyCheckboxText,
        privacyLinkText,
        marketingCheckboxText,
        marketingLinkText,
    };
};
